import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Grid, GridCell } from './Grid';
import { Breadcrumbs } from './Breadcrumb';
import Headline3 from './Typography/Heading/Headline3';

const JobsContainer = styled.div`
  margin: 0px auto;
  padding: 24px;
  max-width: 970px;
  box-sizing: border-box;
  color: #FFF;
  z-index: 3;
`;

const JobsHeader = styled.div`
  margin: 0 auto;
  padding: 24px 0;
  text-align: center;
`;

const JobsHeadline = styled(Headline3)`
  && {
    margin-bottom: 16px;
    padding: 0 16px;
  }
`;

const JobsMain = styled(Grid)`
  && a {
    text-decoration: none;
  }
  && {
    margin: 0;
    padding: 0;
    width: 100%;
  }
  color: var(--black);
  background-color: var(--white);
  line-height: 1.8;
  font-size: 108%;
  border: 1px solid var(--grey-light);
  && button {
    background: var(--primary-color);
    color: white;
    border: 2px solid var(--primary-color);
    text-align: center;
    margin-left: 4px;
    white-space: nowrap;
  }
`;

const JobsFooter = styled.div`
  color: var(--black);
  font-family: var(--font-secondary);
  font-size: var(--size-body1);
  padding: 4px 0 0 4px;
`;

const JobsBackground = styled.div`
  background-color: #f5f5f5;
  width: 100vw;
  min-height: 100vh;
  position: fixed;
  z-index: -1;
  top: 0;
  left: 0;
`;

const JobApplyBreadcrumbs = styled(Breadcrumbs)`
  padding-left: 4px;
  margin-bottom: 0;
  height: 24px;
  :before {
    display: none;
  }
`;

export const JobApplyFormControlLabel = styled(FormControlLabel)`
  &.MuiFormControlLabel-root {
    margin: 0;
    display: inline;
  }

  .MuiFormControlLabel-label {
    color: var(--black-medium);
    font-family: var(--font-primary);
    transform: none;
    letter-spacing: unset;
  }

  .MuiButtonBase-root {
    padding: 3px;
    display: inline;
  }

  .MuiTypography-root {
    position: relative;
    bottom: 3px;
  }
`;

export const JobApplyContainer = ({
  children,
  title,
  backButton,
  breadcrumbLinks,
}) => (
  <>
    <JobsContainer>
      {title && (
        <JobsHeader>
          <JobsHeadline tag="h1">
            {title}
          </JobsHeadline>
        </JobsHeader>
      )}
      <div>
        <JobApplyBreadcrumbs
          crumbs={breadcrumbLinks}
        />
      </div>
      <JobsMain spacing={10}>
        {children}
      </JobsMain>
      <JobsFooter>
        {backButton}
      </JobsFooter>
    </JobsContainer>
    <JobsBackground />
  </>
);

JobApplyContainer.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  backButton: PropTypes.node,
  breadcrumbLinks: PropTypes.arrayOf(
    PropTypes.node,
  ).isRequired,
};

JobApplyContainer.defaultProps = {
  children: undefined,
  backButton: undefined,
};

const JobFormPanel = styled(GridCell)`
  && {
    padding: 40px 40px 20px;
  }

  background-color: #FFF;

  .form-item {
    margin-bottom: 24px;
  }

  .form-item > div {
    width: 100%
  }
`;

const JobInfoPanel = styled(GridCell)`
  && {
    padding: 40px;
  }

  border-left: 1px solid var(--grey-light);
  color:  var(--black);
  font-size: var(--size-h5);
  font-family: var(--font-secondary);
`;

const JobDetailList = styled.ul`
  padding: 0;
  margin: 8px 0 0;
`;

const JobDetailListItem = styled.li`
  list-style-type: none;
  padding: 0;
  margin-bottom: 0;
  color: var(--grey-dark);
`;

const JobDetailLabel = styled.span`
  width: 33%;
  display: inline-block;
`;

const JobDetailValue = styled.span`
  width: 67%;
  display: inline-block;
`;

const PrivacyDisclaimer = styled.p`
  font-family: var(--font-secondary);
  font-size: var(--size-body);
  line-height: 1.25rem;
  letter-spacing: .01471rem;
  color: var(--black-medium);

  && a {
    color: var(--grey-dark);
  }
`;

const JobApply = ({
  children, jobDetails, privacyDisclaimer,
}) => (
  <>
    <JobFormPanel xs={12} md={6}>
      {children}
    </JobFormPanel>
    <JobInfoPanel xs={12} md={6}>
      <JobDetailList>
        {
          jobDetails.map(
            ({ label, value }) => (
              <JobDetailListItem key={label}>
                <JobDetailLabel>
                  {label}
                </JobDetailLabel>
                <JobDetailValue>
                  {value}
                </JobDetailValue>
              </JobDetailListItem>
            ),
          )
        }
      </JobDetailList>
      <hr />
      <PrivacyDisclaimer
        dangerouslySetInnerHTML={
          { __html: privacyDisclaimer }
        }
      />
    </JobInfoPanel>
  </>
);

JobApply.propTypes = {
  children: PropTypes.node.isRequired,
  jobDetails: PropTypes.arrayOf(
    PropTypes.shape({}),
  ),
  privacyDisclaimer: PropTypes.string,
};

JobApply.defaultProps = {
  jobDetails: undefined,
  privacyDisclaimer: undefined,
};

export default JobApply;
